import type { CancellationActorType, ServiceMode } from '../enums';
import { CANCELLATION_ACTOR_TYPES, SERVICE_MODES } from '../enums';
import type { ReportSpecificFilter, ReportTableColumn } from '../report-runtime';
import { REPORT_PAYMENT_STATES, type ReportPaymentState, type ServiceJobDetailRow } from '../reports';
export interface CancelledServicesFilters {
  actorType?: (CancellationActorType | 'UNKNOWN')[];
  reasonId?: string;
  reasonCode?: string;
  paymentState?: ReportPaymentState[];
  requiresFinanceReview?: boolean;
  hasEvent?: boolean;
  serviceMode?: ServiceMode[];
  passengerId?: string;
  agentId?: string;
}
export interface CancelledServicesEvent {
  cancelEventId: string | null;
  cancelledAt: string | null;
  cancelActorType: CancellationActorType | null;
  cancelActorId: string | null;
  cancelActorName: string | null;
  cancelActorPictureUrl: string | null;
  cancelReasonId: string | null;
  cancelReasonCode: string | null;
  cancelReasonLabel: string | null;
  cancelNote: string | null;
  passengerReason: string | null;
}
export interface CancelledServicesRow extends CancelledServicesEvent {
  trip: ServiceJobDetailRow;
  bookingId: string;
  pickupApptAt: string | null;
  arriveByAt: string | null;
}
export const cancelledServicesSpecificFilters: readonly ReportSpecificFilter[] = [
  { id: 'actorType', type: 'multi', values: [...CANCELLATION_ACTOR_TYPES, 'UNKNOWN'] },
  { id: 'reasonId', type: 'uuid' },
  { id: 'reasonCode', type: 'text' },
  { id: 'paymentState', type: 'multi', values: REPORT_PAYMENT_STATES },
  { id: 'requiresFinanceReview', type: 'boolean' },
  { id: 'hasEvent', type: 'boolean' },
  { id: 'serviceMode', type: 'multi', values: SERVICE_MODES },
  { id: 'passengerId', type: 'uuid' },
  { id: 'agentId', type: 'uuid' },
];
export const cancelledServicesSorts = ['cancelledAt', 'tripRef', 'serviceDate', 'createdAt'] as const;
export const cancelledServicesDefaultColumns = [
  'provider_name',
  'trip_ref',
  'service_date',
  'cancelled_at',
  'cancel_actor_type',
  'cancel_actor_name',
  'cancel_reason_label',
  'passenger_name',
  'agent_name',
  'current_total',
  'received_total',
  'requires_finance_review',
] as const;
export const cancelledServicesColumns: readonly ReportTableColumn[] = [
  {
    id: 'provider_id',
    labelKey: 'cancelledServices.column.provider_id',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'provider_name',
    labelKey: 'cancelledServices.column.provider_name',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'trip_id',
    labelKey: 'cancelledServices.column.trip_id',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'trip_ref',
    labelKey: 'cancelledServices.column.trip_ref',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    sortable: true,
    sortKey: 'tripRef',
  },
  {
    id: 'booking_id',
    labelKey: 'cancelledServices.column.booking_id',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'trip_status',
    labelKey: 'cancelledServices.column.trip_status',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'trip_leg',
    labelKey: 'cancelledServices.column.trip_leg',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'service_mode',
    labelKey: 'cancelledServices.column.service_mode',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'dispatch_mode',
    labelKey: 'cancelledServices.column.dispatch_mode',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'created_at',
    labelKey: 'cancelledServices.column.created_at',
    type: 'timestamp',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    sortable: true,
    sortKey: 'createdAt',
  },
  {
    id: 'service_date',
    labelKey: 'cancelledServices.column.service_date',
    type: 'timestamp',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.meaning.serviceDate',
    sortable: true,
    sortKey: 'serviceDate',
  },
  {
    id: 'service_date_basis',
    labelKey: 'cancelledServices.column.service_date_basis',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.meaning.serviceDate',
  },
  {
    id: 'scheduled_start',
    labelKey: 'cancelledServices.column.scheduled_start',
    type: 'timestamp',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'scheduled_end',
    labelKey: 'cancelledServices.column.scheduled_end',
    type: 'timestamp',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'pickup_appt_at',
    labelKey: 'cancelledServices.column.pickup_appt_at',
    type: 'timestamp',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'arrive_by_at',
    labelKey: 'cancelledServices.column.arrive_by_at',
    type: 'timestamp',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'cancel_event_id',
    labelKey: 'cancelledServices.column.cancel_event_id',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'cancelled_at',
    labelKey: 'cancelledServices.column.cancelled_at',
    type: 'timestamp',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.source.event',
    sortable: true,
    sortKey: 'cancelledAt',
  },
  {
    id: 'date_basis',
    labelKey: 'cancelledServices.column.date_basis',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.source.event',
  },
  {
    id: 'cancel_actor_type',
    labelKey: 'cancelledServices.column.cancel_actor_type',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'cancel_actor_id',
    labelKey: 'cancelledServices.column.cancel_actor_id',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'cancel_actor_name',
    labelKey: 'cancelledServices.column.cancel_actor_name',
    type: 'text',
    source: 'cancelledServices.source.actorSnapshot',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'cancel_reason_id',
    labelKey: 'cancelledServices.column.cancel_reason_id',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'cancel_reason_code',
    labelKey: 'cancelledServices.column.cancel_reason_code',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'cancel_reason_label',
    labelKey: 'cancelledServices.column.cancel_reason_label',
    type: 'text',
    source: 'cancelledServices.source.reason',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'cancel_reason_label_source',
    labelKey: 'cancelledServices.column.cancel_reason_label_source',
    type: 'text',
    source: 'cancelledServices.source.reason',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'cancel_note',
    labelKey: 'cancelledServices.column.cancel_note',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'passenger_reason',
    labelKey: 'cancelledServices.column.passenger_reason',
    type: 'text',
    source: 'cancelledServices.source.event',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'passenger_id',
    labelKey: 'cancelledServices.column.passenger_id',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'passenger_code',
    labelKey: 'cancelledServices.column.passenger_code',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'passenger_name',
    labelKey: 'cancelledServices.column.passenger_name',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'passenger_phone',
    labelKey: 'cancelledServices.column.passenger_phone',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'agent_id',
    labelKey: 'cancelledServices.column.agent_id',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'agent_code',
    labelKey: 'cancelledServices.column.agent_code',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'agent_name',
    labelKey: 'cancelledServices.column.agent_name',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'agent_phone',
    labelKey: 'cancelledServices.column.agent_phone',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'vehicle_id',
    labelKey: 'cancelledServices.column.vehicle_id',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'vehicle_plate',
    labelKey: 'cancelledServices.column.vehicle_plate',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'service_area_ids',
    labelKey: 'cancelledServices.column.service_area_ids',
    type: 'list',
    source: 'cancelledServices.source.currentScope',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'service_area_names',
    labelKey: 'cancelledServices.column.service_area_names',
    type: 'list',
    source: 'cancelledServices.source.currentScope',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'service_group_id',
    labelKey: 'cancelledServices.column.service_group_id',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'service_group_name',
    labelKey: 'cancelledServices.column.service_group_name',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'scope_provenance',
    labelKey: 'cancelledServices.column.scope_provenance',
    type: 'text',
    source: 'cancelledServices.source.currentScope',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'origin_label',
    labelKey: 'cancelledServices.column.origin_label',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'destination_label',
    labelKey: 'cancelledServices.column.destination_label',
    type: 'text',
    source: 'cancelledServices.source.currentTrip',
    emptyMeaning: 'cancelledServices.empty',
    pii: true,
  },
  {
    id: 'currency',
    labelKey: 'cancelledServices.column.currency',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'fare_state',
    labelKey: 'cancelledServices.column.fare_state',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'quote_provenance',
    labelKey: 'cancelledServices.column.quote_provenance',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'quoted_total',
    labelKey: 'cancelledServices.column.quoted_total',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'current_total',
    labelKey: 'cancelledServices.column.current_total',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'final_total',
    labelKey: 'cancelledServices.column.final_total',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.meaning.finalTotal',
  },
  {
    id: 'fare_adjustment_delta',
    labelKey: 'cancelledServices.column.fare_adjustment_delta',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.meaning.adjustment',
  },
  {
    id: 'fare_base',
    labelKey: 'cancelledServices.column.fare_base',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'fare_overage_distance',
    labelKey: 'cancelledServices.column.fare_overage_distance',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'fare_overage_time',
    labelKey: 'cancelledServices.column.fare_overage_time',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'fare_surcharge_total',
    labelKey: 'cancelledServices.column.fare_surcharge_total',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'fare_computed_at',
    labelKey: 'cancelledServices.column.fare_computed_at',
    type: 'timestamp',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'billing_distance_source',
    labelKey: 'cancelledServices.column.billing_distance_source',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'billing_duration_source',
    labelKey: 'cancelledServices.column.billing_duration_source',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'billable_distance_km',
    labelKey: 'cancelledServices.column.billable_distance_km',
    type: 'distance',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'billable_duration_min',
    labelKey: 'cancelledServices.column.billable_duration_min',
    type: 'number',
    unitKey: 'unit_minutes',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'fare_is_free',
    labelKey: 'cancelledServices.column.fare_is_free',
    type: 'boolean',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'base_payment_status',
    labelKey: 'cancelledServices.column.base_payment_status',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'base_amount_due',
    labelKey: 'cancelledServices.column.base_amount_due',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'base_amount_received',
    labelKey: 'cancelledServices.column.base_amount_received',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'base_outstanding',
    labelKey: 'cancelledServices.column.base_outstanding',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'base_due_by',
    labelKey: 'cancelledServices.column.base_due_by',
    type: 'timestamp',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'base_verified_at',
    labelKey: 'cancelledServices.column.base_verified_at',
    type: 'timestamp',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'overage_payment_status',
    labelKey: 'cancelledServices.column.overage_payment_status',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'overage_amount_due',
    labelKey: 'cancelledServices.column.overage_amount_due',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'overage_amount_received',
    labelKey: 'cancelledServices.column.overage_amount_received',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'overage_outstanding',
    labelKey: 'cancelledServices.column.overage_outstanding',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'overage_due_by',
    labelKey: 'cancelledServices.column.overage_due_by',
    type: 'timestamp',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'overage_verified_at',
    labelKey: 'cancelledServices.column.overage_verified_at',
    type: 'timestamp',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
  },
  {
    id: 'billed_total',
    labelKey: 'cancelledServices.column.billed_total',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.source.finance',
  },
  {
    id: 'received_total',
    labelKey: 'cancelledServices.column.received_total',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.meaning.received',
  },
  {
    id: 'outstanding_total',
    labelKey: 'cancelledServices.column.outstanding_total',
    type: 'money',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.source.finance',
  },
  {
    id: 'payment_state',
    labelKey: 'cancelledServices.column.payment_state',
    type: 'text',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.meaning.review',
  },
  {
    id: 'requires_finance_review',
    labelKey: 'cancelledServices.column.requires_finance_review',
    type: 'boolean',
    source: 'cancelledServices.source.finance',
    emptyMeaning: 'cancelledServices.empty',
    meaningKey: 'cancelledServices.meaning.review',
  },
];
